import React, { useEffect, useMemo, useRef } from "react";
import { ProgressLevelData } from "../OlympianCardV2";
import { Box, Typography } from "@mui/material";
import { ITime } from "../ProgressLevelModal/ProgressLevelModal";
import MilestoneProgressBar from "../OlympianCardV2/MilestoneProgressBar";

interface ProgressLevelMobileProps {
    levels: any[];
    gameUserLevel: ProgressLevelData | null;
    time: ITime;
    completedLevels: number;
}

const ProgressLevelMobile = ({
    levels,
    gameUserLevel,
    time,
    completedLevels,
}: ProgressLevelMobileProps) => {
    const isReachedMaxLevel = useMemo(() => {
        return completedLevels === levels.length - 1;
    }, [levels, completedLevels]);

    const nextLevelIndex: number = Math.min(
        completedLevels + 1,
        levels.length - 1
    );
    const nextLevelName: string =
        levels?.length > 0 ? levels[nextLevelIndex].name : "";

    const levelRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const currentLevelRef = levelRefs.current[completedLevels];
        if (currentLevelRef) {
            currentLevelRef.scrollIntoView({
                block: "center",
                behavior: "auto",
            });
        }
    }, []);

    return (
        <>
            <Box
                sx={{
                    boxShadow: "#93939340 0px 25px 20px -20px",
                    position: "fixed",
                    top: "0px",
                    left: "0px",
                    zIndex: "99",
                    backgroundColor: "#fff",
                    width: "100%",
                    // padding: "12px 8px",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: {
                            xs: "0px",
                            md: "0 0 0 30px",
                        },
                        height: "110px",
                    }}
                >
                    <Box
                        sx={{
                            width: "100px",
                            height: "100px",
                            marginRight: "16px",
                        }}
                    >
                        <img
                            src={`${
                                gameUserLevel?.metadata?.current?.iconUrl.split(
                                    ".png"
                                )[0]
                            }_128x128.png`}
                            alt="Aviron Level Achievement"
                            width={"100%"}
                            height={"100%"}
                            style={{
                                objectFit: "contain",
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "GRIFTER",
                                fontWeight: 700,
                                fontSize: "24px",
                                marginBottom: "12px",
                            }}
                        >
                            {gameUserLevel?.metadata?.current?.name?.toUpperCase()}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <img
                                alt="icon-time"
                                src="/images/icon-time.png"
                                style={{
                                    width: "21px",
                                    height: "18px",
                                    objectFit: "contain",
                                    marginRight: "8px",
                                }}
                            />
                            {time.hour > 0 && (
                                <Typography
                                    sx={{
                                        fontFamily: "MonoSpec",
                                        fontWeight: 500,
                                        fontSize: "16px",
                                        marginRight: "8px",
                                    }}
                                >
                                    {time.hour} {time.hour > 1 ? "HRS" : "HR"}
                                </Typography>
                            )}
                            <Typography
                                sx={{
                                    fontFamily: "MonoSpec",
                                    fontWeight: 500,
                                    fontSize: "16px",
                                }}
                            >
                                {time.minute} {time.minute > 1 ? "MINS" : "MIN"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    padding: "0px 12px 50px",
                    marginTop: "140px",
                }}
            >
                <Box>
                    {levels.map((level, index) => {
                        const isCurrentLevel = completedLevels === index;
                        const isOverLevel = completedLevels >= index;
                        return (
                            <Box
                                key={index}
                                ref={(el) => (levelRefs.current[index] = el as any)} 
                                sx={{
                                    backgroundColor: isOverLevel
                                        ? "#fff"
                                        : "transparent",
                                    boxShadow: isOverLevel
                                        ? "rgba(99, 99, 99, 12%) 0px 2px 8px 0px"
                                        : "none",
                                    padding: "12px",
                                    marginBottom: "12px",
                                    // marginTop: index === 0 ? "170px" : "0px",
                                    borderRadius: "12px",
                                    minHeight: isCurrentLevel
                                        ? "175px"
                                        : "auto",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: "50px",
                                                height: "50px",
                                                marginRight: "12px",
                                            }}
                                        >
                                            <img
                                                src={`${
                                                    isOverLevel
                                                        ? level.icon
                                                        : level.iconGray
                                                }`}
                                                alt="Aviron Achievement"
                                                loading="eager"
                                                width={"100%"}
                                                height={"100%"}
                                                style={{
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "14px",
                                                letterSpacing: "0.2px",
                                                color: "#232323",
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {level.name}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 500,
                                            lineHeight: "18px",
                                            color: isCurrentLevel
                                                ? "#161616"
                                                : "#939393",
                                            display:
                                                index === 0 ? "none" : "block",
                                            fontFamily: "MonoSpec",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {level.time}
                                    </Typography>
                                </Box>
                                {isCurrentLevel && (
                                    <Box>
                                        <MilestoneProgressBar
                                            data={
                                                gameUserLevel?.milestones
                                                    .details || []
                                            }
                                            percentProcess={
                                                gameUserLevel?.progress || 0
                                            }
                                            isShowLabel
                                            isReachedMaxLevel={
                                                isReachedMaxLevel
                                            }
                                            nextLevelName={nextLevelName}
                                            gameUserLevel={gameUserLevel}
                                        />
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </>
    );
};

export default ProgressLevelMobile;
